import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Sparkles } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import useFeatureInterest from '@/services/api/billing/useFeatureInterest'
import type { ResolvedFeature } from './useFeature'

interface UpgradeModalProps {
  feature: Pick<ResolvedFeature, 'lookupKey' | 'name'>
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * Upsell dialog for a locked feature. On open it records the interest signal
 * (fire-and-forget) so marketing can follow up.
 */
export default function UpgradeModal({
  feature,
  open,
  onOpenChange,
}: UpgradeModalProps) {
  const navigate = useNavigate()
  const featureInterest = useFeatureInterest()

  useEffect(() => {
    if (open) featureInterest.mutate(feature.lookupKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, feature.lookupKey])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="size-4 text-primary" />
            {feature.name}
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          <DialogDescription>
            <strong>{feature.name}</strong> isn&apos;t included in your current
            plan. Upgrade to unlock it and more.
          </DialogDescription>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Not now
          </Button>
          <Button
            onClick={() => {
              onOpenChange(false)
              navigate('/billing')
            }}
          >
            View plans
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
